import {
  Badge,
  Box,
  Button,
  Flex,
  Select,
  Spinner,
  Text,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { typography } from 'theme/tokens';
import { translate } from '../../i18n';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import {
  AdminNotificationFeedItemDto,
  AdminNotificationType,
} from '../../models/admin-notification/dto/admin-notification-feed.dto';
import ScopePrompt from '../_shared/ScopePrompt';
import { classifyRead, READ_FAILED_I18N_KEY } from '../_shared/readState';
import { formatRelativeTime, getTypeColorScheme } from './utils/admin-notification.utils';
import { useGetAdminNotifications } from './hooks/useGetAdminNotifications';
import { useMarkAsRead } from './hooks/useMarkAsRead';
import { useMarkAllAsRead } from './hooks/useMarkAllAsRead';

export default function AdminNotificationListPage() {
  const { selectedOrganization } = useSelectedOrganization();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [typeFilter, setTypeFilter] = useState<AdminNotificationType | undefined>();
  const [readFilter, setReadFilter] = useState<string | undefined>();
  const isMobile = useBreakpointValue({ base: true, lg: false });

  // `useGetAdminNotifications` is `enabled: !!organizationId`, and React Query
  // v4 reports a DISABLED query as `isLoading: true` / `fetchStatus: 'idle'`,
  // so the raw flag this page used to read drove the spinner FOREVER with no
  // organization selected — there is no scope guard here to catch it.
  //
  // The worse half was the arm BELOW that spinner. This page took no error flag
  // at all, so a REFUSED read (`data === undefined`, `isLoading === false`)
  // fell through `data?.notifications ?? []` straight into
  // `admin_notifications.empty` — "No notifications yet", stated confidently
  // about a question the API declined to answer. That is the campaign's worst
  // shape (R89-1, and run 107's `LocationListPage`): an operator can disbelieve
  // a spinner, and has no way to disbelieve a sentence.
  const { data, isInitialLoading, isError } = useGetAdminNotifications({
    organizationId: selectedOrganization?.id,
    page,
    limit: 20,
    type: typeFilter,
    isRead: readFilter,
  });
  const readState = classifyRead(isError, isInitialLoading, data);
  const markAsRead = useMarkAsRead(selectedOrganization?.id);
  const markAllAsRead = useMarkAllAsRead(selectedOrganization?.id);

  const textPrimary = useColorModeValue('secondaryGray.900', 'white');
  const textSecondary = useColorModeValue('secondaryGray.600', 'gray.400');
  const cardBg = useColorModeValue('white', 'navy.800');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const unreadBg = useColorModeValue('brand.50', 'whiteAlpha.50');
  const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');

  const notifications = data?.notifications ?? [];
  const pagination = data?.pagination;
  const unreadCount = data?.unreadCount ?? 0;

  function handleClick(n: AdminNotificationFeedItemDto) {
    if (!n.isRead) markAsRead.mutate(n.id);
    if (n.data?.gameId) navigate(`/admin/gameDetails?game=${n.data.gameId}`);
  }

  return (
    <Box pt="20px">
      {/* Header */}
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap="12px"
        mb="20px"
      >
        <Text fontSize="xl" fontWeight="700" color={textPrimary}>
          {translate('admin_notifications.title' as any)}
          {unreadCount > 0 && (
            <Badge ml="8px" colorScheme="brand" borderRadius="full" px="8px" py="2px">
              {unreadCount} {translate('admin_notifications.unread' as any)}
            </Badge>
          )}
        </Text>
        {unreadCount > 0 && (
          <Button size="sm" variant="outline" colorScheme="brand" onClick={() => markAllAsRead.mutate()}>
            {translate('admin_notifications.mark_all_read' as any)}
          </Button>
        )}
      </Flex>

      {/* Filters */}
      <Flex gap="12px" mb="16px" wrap="wrap">
        <Select
          size="sm"
          w={{ base: '100%', sm: '200px' }}
          placeholder={translate('admin_notifications.filter_all_types' as any)}
          value={typeFilter ?? ''}
          onChange={(e) => {
            setTypeFilter(e.target.value ? (e.target.value as AdminNotificationType) : undefined);
            setPage(0);
          }}
        >
          {Object.values(AdminNotificationType).map(t => (
            <option key={t} value={t}>
              {translate(`admin_notifications.type_${t}` as any)}
            </option>
          ))}
        </Select>
        <Select
          size="sm"
          w={{ base: '100%', sm: '160px' }}
          placeholder={translate('admin_notifications.filter_all_status' as any)}
          value={readFilter ?? ''}
          onChange={(e) => {
            setReadFilter(e.target.value || undefined);
            setPage(0);
          }}
        >
          <option value="false">{translate('admin_notifications.filter_unread' as any)}</option>
          <option value="true">{translate('admin_notifications.filter_read' as any)}</option>
        </Select>
      </Flex>

      {/* Content */}
      <Box bg={cardBg} borderRadius="14px" border="1px solid" borderColor={borderColor} overflow="hidden">
        {readState === 'never-issued' ? (
          <ScopePrompt gate="organization" />
        ) : readState === 'loading' ? (
          <Flex justify="center" py="60px">
            <Spinner size="md" color="brand.500" />
          </Flex>
        ) : readState === 'unreadable' ? (
          // A borrowed key, not a new one: `src/i18n/{en,fr}.ts` sit inside the
          // open-PR fence (R96-7), and `common.unexpected_error` already says
          // the one true thing — we asked and did not get an answer.
          <Flex justify="center" py="60px">
            <Text {...typography.caption} color={textSecondary}>
              {translate(READ_FAILED_I18N_KEY as any)}
            </Text>
          </Flex>
        ) : notifications.length === 0 ? (
          <Flex justify="center" py="60px">
            <Text {...typography.caption} color={textSecondary}>
              {translate('admin_notifications.empty' as any)}
            </Text>
          </Flex>
        ) : (
          <Box>
            {notifications.map((n: AdminNotificationFeedItemDto) => (
              <Flex
                key={n.id}
                direction={{ base: 'column', md: 'row' }}
                align={{ base: 'flex-start', md: 'center' }}
                gap={{ base: '6px', md: '16px' }}
                px={{ base: '12px', md: '20px' }}
                py="12px"
                cursor="pointer"
                bg={n.isRead ? 'transparent' : unreadBg}
                _hover={{ bg: hoverBg }}
                borderBottom="1px solid"
                borderColor={borderColor}
                onClick={() => handleClick(n)}
              >
                {/* Top row: type badge + time */}
                <Flex
                  align="center"
                  justify="space-between"
                  w={{ base: '100%', md: '200px' }}
                  gap="8px"
                  flexShrink={0}
                >
                  <Badge
                    colorScheme={getTypeColorScheme(n.type)}
                    borderRadius="full"
                    px="8px"
                    fontSize="xs"
                    whiteSpace="nowrap"
                    w="180px"
                    textAlign="center"
                  >
                    {translate(`admin_notifications.type_${n.type}` as any)}
                  </Badge>
                  {isMobile && (
                    <Flex align="center" gap="6px">
                      {!n.isRead && (
                        <Box w="6px" h="6px" borderRadius="full" bg="brand.500" />
                      )}
                      <Text {...typography.caption} color={textSecondary}>
                        {formatRelativeTime(n.createdAt)}
                      </Text>
                    </Flex>
                  )}
                </Flex>

                {/* Title + message */}
                <Box flex={1} minW={0}>
                  <Text
                    {...typography.bodyBold}
                    color={textPrimary}
                    noOfLines={1}
                    fontWeight={n.isRead ? '400' : '600'}
                  >
                    {n.title}
                  </Text>
                  <Text {...typography.caption} color={textSecondary} noOfLines={2}>
                    {n.message}
                  </Text>
                </Box>

                {/* Desktop: date + status */}
                {!isMobile && (
                  <Flex align="center" gap="16px" flexShrink={0}>
                    <Text {...typography.caption} color={textSecondary} whiteSpace="nowrap">
                      {new Date(n.createdAt).toLocaleString()}
                    </Text>
                    <Badge colorScheme={n.isRead ? 'gray' : 'brand'} borderRadius="full">
                      {n.isRead
                        ? translate('admin_notifications.read' as any)
                        : translate('admin_notifications.unread' as any)}
                    </Badge>
                  </Flex>
                )}
              </Flex>
            ))}
          </Box>
        )}
      </Box>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <Flex justify="center" gap="8px" mt="16px">
          <Button
            size="sm"
            isDisabled={!pagination.hasPrevPage}
            onClick={() => setPage(p => p - 1)}
          >
            {translate('admin_notifications.prev' as any)}
          </Button>
          <Flex align="center">
            <Text {...typography.caption} color={textSecondary}>
              {pagination.currentPage + 1} / {pagination.totalPages}
            </Text>
          </Flex>
          <Button
            size="sm"
            isDisabled={!pagination.hasNextPage}
            onClick={() => setPage(p => p + 1)}
          >
            {translate('admin_notifications.next' as any)}
          </Button>
        </Flex>
      )}
    </Box>
  );
}
